import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import VideoCard from '@/components/feature/VideoCard';
import { categories } from '@/mocks/categories';
import { searchVideos } from '@/utils/videoUtils';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function Search() {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') || '';
  const [input, setInput] = useState(query);

  const results = useMemo(() => searchVideos(query), [query]);

  usePageMeta({
    title: query ? `“${query}”的搜索结果 - 在线视频` : '搜索视频 - 在线视频',
    description: query
      ? `在线视频为你找到与“${query}”相关的 ${results.length} 个视频结果，支持电影、电视剧、综艺与短视频。`
      : '在在线视频搜索你想看的电影、电视剧、综艺和短视频。',
    keywords: query ? `${query},搜索,在线视频,在线观看` : '视频搜索,在线视频',
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    setParams(q ? { q } : {});
  };

  const popularCategories = categories;

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-6 py-8">
        <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground-950">搜索视频</h1>
        <p className="mt-2 text-sm text-foreground-500">
          输入关键词，快速找到感兴趣的电影、电视剧、综艺与短视频
        </p>

        <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-2xl">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入视频标题、类型或标签关键词"
              className="w-full h-12 pl-11 pr-4 rounded-md border border-background-300 bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
            />
          </div>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 px-8 h-12 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
          >
            <i className="ri-search-line"></i>
            搜索
          </button>
        </form>

        {query && (
          <p className="mt-6 text-sm text-foreground-600">
            {results.length > 0 ? (
              <>
                关于 <span className="font-semibold text-primary-700">“{query}”</span> 共找到{' '}
                <span className="font-semibold text-accent-600">{results.length}</span> 个视频结果
              </>
            ) : (
              <>
                关于 <span className="font-semibold text-primary-700">“{query}”</span> 的结果
              </>
            )}
          </p>
        )}
      </div>

      <div className="w-full px-4 md:px-6 pb-12">
        {!query ? (
          <div className="rounded-lg bg-background-100 border border-background-200 py-16 text-center">
            <i className="ri-search-2-line text-5xl text-foreground-300"></i>
            <h2 className="mt-4 font-display text-lg font-bold text-foreground-950">
              想看点什么呢？
            </h2>
            <p className="mt-2 text-sm text-foreground-500">
              输入关键词开始搜索，或从下方热门分类中发现好内容。
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              {popularCategories.map((c) => (
                <Link
                  key={c.key}
                  to={`/category/${c.key}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50 border border-background-300 hover:border-primary-400 text-sm text-foreground-800 transition-colors cursor-pointer"
                >
                  <i className={c.icon}></i>
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        ) : results.length === 0 ? (
          <div className="rounded-lg bg-background-100 border border-background-200 py-16 text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
            <h2 className="mt-4 font-display text-lg font-bold text-foreground-950">
              没有找到相关视频
            </h2>
            <p className="mt-2 text-sm text-foreground-500">
              换个关键词试试，或者去下面的热门分类逛逛吧。
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              {popularCategories.map((c) => (
                <Link
                  key={c.key}
                  to={`/category/${c.key}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-100 text-primary-700 hover:bg-primary-200 text-sm font-medium transition-colors cursor-pointer"
                >
                  <i className={c.icon}></i>
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
            {results.map((video) => (
              <VideoCard key={video.id} video={video} showDescription />
            ))}
          </div>
        )}
      </div>
    </SiteLayout>
  );
}